/* eslint-disable react/require-default-props */
import React, {useState} from 'react';
import {StyleProp, useWindowDimensions, ViewStyle} from 'react-native';
import {createMaterialTopTabNavigator} from '@react-navigation/material-top-tabs';
import {NavigatorScreenParams} from '@react-navigation/native';
import {isFunction} from 'lodash';

import {indent} from '/constant/layout.constant';
import {useTranslation} from '/provider/TranslationProvider';

import {renderBarLabel} from './BarLabel';
import {styles, TAB_BAR_HEIGH} from './TabLayout.style';
import {getAccessibilityLabel} from './TabLayout.util';

const ScreenTab = createMaterialTopTabNavigator();

interface TabItem {
  label: string;
  name: string;
  component: React.ComponentType<any>;
  onTabPress?: () => void;
}

interface TabLayoutProps {
  tabs?: TabItem[];
  style?: {
    tabBarIndicatorStyle?: StyleProp<ViewStyle>;
    tabBarIndicatorContainerStyle?: StyleProp<ViewStyle>;
    tabBarStyle?: StyleProp<ViewStyle>;
    tabBarItemStyle?: StyleProp<ViewStyle>;
    sceneContainerStyle?: StyleProp<ViewStyle>;
  };
  swipeEnabled?: boolean;
  screenOptions?: object;
  tabNavigator?: NavigatorScreenParams<any>;
  initialRouteName?: string;
  isPeopleToContact?: boolean;
}

const TabLayout: React.FC<TabLayoutProps> = ({
  tabs = [],
  style: styleProp = {},
  screenOptions: screenOptionsProp = {},
  tabNavigator = {},
  swipeEnabled = true,
  initialRouteName = '',
  isPeopleToContact = false,
}) => {
  const {ts} = useTranslation('component.layout.TabLayout');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const {fontScale = 1, width = 0} = useWindowDimensions();
  const clampedFontScale = Math.max(1, Math.min(fontScale, 2));
  const isFontResizeActive = clampedFontScale > 1;
  const totalTabsWidth = tabs.reduce((totalWidth, {label = ''}) => {
    const labelLength = String(label).length;
    const estimatedTabWidth = Math.max(100, labelLength * 8 * clampedFontScale);
    return totalWidth + estimatedTabWidth;
  }, 0);
  const shouldUseScrollableTabs =
    tabs.length > 0 && totalTabsWidth > Math.max(320, width - indent * 2);
  const isSwipeEnabled = swipeEnabled || isFontResizeActive;

  const generateOnTabPress = (
    onTabPress: (() => void) | undefined,
    index: number,
    isFocus = false,
  ) => {
    const isTabChange = activeTabIndex !== index;
    setActiveTabIndex(index);

    if (isFocus && !isTabChange) {
      return;
    }

    if (isFunction(onTabPress)) {
      onTabPress();
    }
  };

  const screenOptions = ({
    route: {name: routeName},
  }: {
    route: {name: string};
  }) => ({
    swipeEnabled: isSwipeEnabled,
    // Ensure tab headers are always tappable regardless of swipe setting
    tabBarPressColor: 'rgba(0, 0, 0, 0.1)',
    tabBarPressOpacity: 0.8,
    tabBarTestID: tabs.find(({name}) => name === routeName)?.label,
    tabBarLabel: renderBarLabel({routeName, tabs}),
    tabBarIndicatorStyle: [
      styles.tabBarIndicatorStyle,
      styleProp.tabBarIndicatorStyle,
    ],
    tabBarIndicatorContainerStyle: [
      styles.tabBarIndicatorContainerStyle,
      {marginTop: indent * 2.3 * clampedFontScale},
      styleProp.tabBarIndicatorContainerStyle,
    ],
    tabBarStyle: [
      styles.tabBar,
      {minHeight: TAB_BAR_HEIGH * clampedFontScale},
      styleProp.tabBarStyle,
    ],
    tabBarScrollEnabled: shouldUseScrollableTabs,
    tabBarItemStyle: [
      shouldUseScrollableTabs
        ? {
            width: 'auto',
            minWidth: 100 * clampedFontScale,
            paddingHorizontal: indent,
            alignItems: 'center',
          }
        : {
            flex: 1,
            minWidth: 100,
            alignItems: 'center',
          },
      styleProp.tabBarItemStyle,
    ],
    ...screenOptionsProp,
  });

  const renderTab = ({name, component, onTabPress, count}: any, index: any) => (
    <ScreenTab.Screen
      key={name}
      name={name}
      component={component}
      listeners={{
        focus: () => {
          if (isPeopleToContact) {
            generateOnTabPress(onTabPress, index, true);
          }
        },
        tabPress: () => {
          generateOnTabPress(onTabPress, index, false);
        },
      }}
      options={{
        tabBarAccessibilityLabel: ts(
          'tabAccessibilityLabel',
          getAccessibilityLabel({
            title: name,
            count,
            activeTabIndex,
            tabIndex: index,
            totalTabs: tabs.length,
          }),
        ),
      }}
    />
  );

  return (
    <ScreenTab.Navigator
      backBehavior="none"
      screenOptions={screenOptions}
      initialRouteName={initialRouteName}
      sceneContainerStyle={[
        styles.sceneContainer,
        styleProp.sceneContainerStyle,
      ]}
      {...tabNavigator}>
      {tabs.map(renderTab)}
    </ScreenTab.Navigator>
  );
};

export default TabLayout;
